import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/lib/components/Select";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  ElementAnimationDto,
  ElementTranstionDto,
  SceneTypeDto,
} from "@repo/api/overlays";
import ElementTransitionSecondInput from "./ElementTransitionSecondInput";
import {
  CompositionTimings,
  findElementTiming,
} from "@repo/video-engine-i2v/utils/timing";
import { round } from "@/lib/utils";

type Props = {
  element: {
    externalId: string;
    enterAt: ElementTranstionDto;
    exitAt: ElementTranstionDto;
  };
  fieldName: "enterAt" | "exitAt";
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings;
  onTimingChange: () => void;
};

export default function ElementAnimationControls({
  element,
  fieldName,
  sceneType,
  compositionTimings,
  onTimingChange,
}: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);

  const animation = element[fieldName].animation;

  const elementTiming = findElementTiming(
    compositionTimings,
    element.externalId,
    sceneType,
  );

  let maxSeconds = 5;

  if (elementTiming) {
    maxSeconds = Math.min(
      5,
      round(elementTiming.durationInFrames / compositionTimings.fps, 1),
    );
  }

  const primaryLabel = fieldName === "enterAt" ? "Enter at" : "Exit at";

  return (
    <div className="w-full">
      <div className="input-label">
        <span>{primaryLabel}</span>
        <span className="font-normal text-text-secondary"> (animation)</span>
      </div>
      <div className="flex flex-col gap-2">
        <AnimationTypeSelect
          element={element}
          fieldName={fieldName}
          sceneType={sceneType}
          compositionTimings={compositionTimings}
          onTimingChange={onTimingChange}
        />
        <AnimationDirectionSelect
          element={element}
          fieldName={fieldName}
          sceneType={sceneType}
          compositionTimings={compositionTimings}
          onTimingChange={onTimingChange}
        />
        <AnimationEasingSelect
          element={element}
          fieldName={fieldName}
          sceneType={sceneType}
          compositionTimings={compositionTimings}
          onTimingChange={onTimingChange}
        />
        {animation !== null && (
          <ElementTransitionSecondInput
            min={0}
            max={maxSeconds}
            step={0.1}
            seconds={animation.duration}
            onCommitChange={(seconds) => {
              updateElement(element.externalId, (element) => {
                if (element[fieldName].animation === null) {
                  throw new Error("Animation is null");
                }

                element[fieldName].animation.duration = seconds;
              });

              onTimingChange();
            }}
          />
        )}
      </div>
    </div>
  );
}

const animationOptions: Record<
  "enterAt" | "exitAt",
  {
    label: string;
    value: ElementAnimationDto["kind"];
  }[]
> = {
  enterAt: [
    {
      label: "Fade in",
      value: "FADE_IN",
    },
  ],
  exitAt: [
    {
      label: "Fade out",
      value: "FADE_OUT",
    },
  ],
};

function AnimationTypeSelect({ element, fieldName, onTimingChange }: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);

  return (
    <Select
      value={element[fieldName].animation?.kind ?? "NONE"}
      onValueChange={(value) => {
        updateElement(element.externalId, (element) => {
          if (value === "NONE") {
            element[fieldName].animation = null;
          } else if (element[fieldName].animation !== null) {
            element[fieldName].animation.kind =
              value as ElementAnimationDto["kind"];
          } else {
            element[fieldName].animation = {
              kind: value as ElementAnimationDto["kind"],
              direction: "FROM_LEFT",
              duration: 0.5,
              easing: "EASE_IN_OUT",
            };
          }
        });

        onTimingChange();
      }}
    >
      <SelectTrigger
        className={`h-9 ${!element[fieldName].animation?.kind ? "text-text-secondary" : ""}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="NONE">
          {fieldName === "enterAt" ? "No entry animation" : "No exit animation"}
        </SelectItem>
        {animationOptions[fieldName].map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const directionOptions: {
  label: string;
  value: ElementAnimationDto["direction"];
}[] = [
  {
    label: "From left",
    value: "FROM_LEFT",
  },
  {
    label: "From right",
    value: "FROM_RIGHT",
  },
  {
    label: "From top",
    value: "FROM_TOP",
  },
  {
    label: "From bottom",
    value: "FROM_BOTTOM",
  },
];

function AnimationDirectionSelect({
  element,
  fieldName,
  onTimingChange,
}: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);
  const animation = element[fieldName].animation;

  const isAnimationWithDirection = ["SLIDE_IN", "SLIDE_OUT"].includes(
    animation?.kind ?? "",
  );

  if (animation === null || !isAnimationWithDirection) {
    return null;
  }

  return (
    <Select
      value={animation.direction}
      onValueChange={(value) => {
        updateElement(element.externalId, (element) => {
          element[fieldName].animation!.direction =
            value as ElementAnimationDto["direction"];
        });

        onTimingChange();
      }}
    >
      <SelectTrigger className="h-9">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {directionOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const easingOptions: {
  label: string;
  value: ElementAnimationDto["easing"];
}[] = [
  {
    label: "Linear",
    value: "LINEAR",
  },
  {
    label: "Ease in",
    value: "EASE_IN",
  },
  {
    label: "Ease out",
    value: "EASE_OUT",
  },
  {
    label: "Ease in out",
    value: "EASE_IN_OUT",
  },
];

function AnimationEasingSelect({ element, fieldName, onTimingChange }: Props) {
  const updateElement = useEditorStore((state) => state.service.updateElement);
  const animation = element[fieldName].animation;

  if (animation === null) {
    return null;
  }

  return (
    <Select
      value={animation.easing}
      onValueChange={(value) => {
        updateElement(element.externalId, (element) => {
          element[fieldName].animation!.easing =
            value as ElementAnimationDto["easing"];
        });

        onTimingChange();
      }}
    >
      <SelectTrigger className="h-9">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {easingOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
